/**
 * Content for the Manual module (displayed as "Manual" in the UI; internal
 * identifiers keep the original "guide" naming), one file per admin-portal
 * module.
 *
 * Convention: when a PR changes what an admin, CSM, or super admin can see or
 * do in a module, update that module's file here in the same PR. This is the
 * only mechanism keeping the Manual accurate — there is no separate CMS.
 */
import { MODULE_KEYS, type ModuleKey } from "@/lib/adminModules";
import type { ModuleGuide } from "./types";
import { dashboardGuide } from "./dashboard";
import { adminManagementGuide } from "./admin-management";
import { aiGuidelinesGuide } from "./ai-guidelines";
import { aiCostsGuide } from "./ai-costs";
import { agenciesGuide } from "./agencies";
import { counselorsGuide } from "./counselors";
import { collegesGuide } from "./colleges";
import { studentsGuide } from "./students";
import { calendarGuide } from "./calendar";
import { counselorResourcesGuide } from "./counselor-resources";
import { cmsCollectionsGuide } from "./cms-collections";
import { websiteContentGuide } from "./website-content";
import { sessionsGuide } from "./sessions";
import { aiSessionsGuide } from "./ai-sessions";
import { templatesGuide } from "./templates";
import { campaignsGuide } from "./campaigns";

export type { AdminRole, GuideSection, ModuleGuide } from "./types";
export { resolveViewerRole } from "./types";

// Order matches the sidebar in Layout/index.tsx.
export const GUIDE_MODULES: ModuleGuide[] = [
  dashboardGuide,
  adminManagementGuide,
  aiGuidelinesGuide,
  aiCostsGuide,
  agenciesGuide,
  counselorsGuide,
  collegesGuide,
  studentsGuide,
  calendarGuide,
  counselorResourcesGuide,
  cmsCollectionsGuide,
  websiteContentGuide,
  sessionsGuide,
  aiSessionsGuide,
  templatesGuide,
  campaignsGuide,
];

export const GUIDE_MODULES_BY_KEY: Record<string, ModuleGuide> = Object.fromEntries(
  GUIDE_MODULES.map((m) => [m.moduleKey, m]),
);

/** Module labels the Guide page needs. Icons live in @/lib/moduleIcons (shared with the sidebar). */
export const GUIDE_MODULE_META: { key: ModuleKey; label: string }[] = [
  { key: MODULE_KEYS.DASHBOARD, label: "Dashboard" },
  { key: MODULE_KEYS.ADMIN_MANAGEMENT, label: "Admin Management" },
  { key: MODULE_KEYS.AI_GUIDELINES, label: "AI Guidelines" },
  { key: MODULE_KEYS.AI_COSTS, label: "AI Costs" },
  { key: MODULE_KEYS.AGENCIES, label: "Agency Management" },
  { key: MODULE_KEYS.COUNSELORS, label: "Counselor Management" },
  { key: MODULE_KEYS.COLLEGES, label: "Institution Management" },
  { key: MODULE_KEYS.STUDENTS, label: "Student Management" },
  { key: MODULE_KEYS.CALENDAR, label: "Calendar Management" },
  { key: MODULE_KEYS.COUNSELOR_RESOURCES, label: "Counselor Resources" },
  { key: MODULE_KEYS.CMS_COLLECTIONS, label: "Content (CMS)" },
  { key: MODULE_KEYS.WEBSITE_CONTENT, label: "Website Content" },
  { key: MODULE_KEYS.SESSIONS, label: "Session History" },
  { key: MODULE_KEYS.AI_SESSIONS, label: "AI Chat Sessions" },
  { key: MODULE_KEYS.TEMPLATES, label: "Message Templates" },
  { key: MODULE_KEYS.CAMPAIGNS, label: "WhatsApp Campaigns" },
];
